'use client';

import { useState, useCallback } from 'react';

import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import { useTheme, useColorScheme } from '@mui/material/styles';

import { useTranslation } from 'react-i18next';

import { CONFIG } from 'src/config-global';
import { useSettingsContext } from 'src/components/settings';
import { SvgColor } from 'src/components/svg-color';

import type { IconButtonProps } from '@mui/material/IconButton';

// ----------------------------------------------------------------------

type ColorSchemeOption = 'light' | 'dark' | 'system';
const insetMenuItemSx = {
  mx: 1,
  my: 0.5,
  borderRadius: 1,
  transition: 'none !important',
  '&:hover': { backgroundColor: 'action.hover' },
  '&:active': { backgroundColor: 'transparent' },
  '&:focus-visible': { backgroundColor: 'transparent' },
  '& .MuiTouchRipple-root': { display: 'none !important' },
};

export function DarkModeToggle({ sx, ...other }: IconButtonProps) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const settings = useSettingsContext();
  const { setMode } = useColorScheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const effectiveMode = theme.palette.mode;
  const selectedScheme = settings.colorScheme as ColorSchemeOption;
  const currentIconSrc =
    selectedScheme === 'system'
      ? `${CONFIG.site.basePath}/assets/icons/setting/ic-system-mode-outlined.svg`
      : effectiveMode === 'dark'
        ? `${CONFIG.site.basePath}/assets/icons/setting/ic-dark-mode-outlined.svg`
        : `${CONFIG.site.basePath}/assets/icons/setting/ic-light-mode-outlined.svg`;

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSelect = useCallback(
    (scheme: ColorSchemeOption) => {
      settings.onUpdateField('colorScheme', scheme);
      setMode(scheme);
      handleClose();
    },
    [settings, setMode, handleClose]
  );

  return (
    <>
      <Tooltip title={t('theme.tooltip')}>
        <IconButton
          onClick={handleOpen}
          disableRipple
          sx={{
            p: 0,
            width: 40,
            height: 40,
            transition: 'none !important',
            '&:hover': { backgroundColor: 'action.hover' },
            '&:active': { backgroundColor: open ? 'action.hover' : 'transparent' },
            backgroundColor: open ? 'action.hover' : undefined,
            ...sx,
          }}
          {...other}
        >
          <SvgColor
            src={currentIconSrc}
            sx={{ color: 'text.primary' }}
          />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        transitionDuration={150}
        PaperProps={{
          sx: {
            mt: '4px',
            border: 'none',
            boxShadow: (muiTheme) =>
              `inset 0 0 0 1px ${
                muiTheme.palette.mode === 'dark'
                  ? 'rgba(145, 158, 171, 0.24)'
                  : 'rgba(145, 158, 171, 0.32)'
              }, 0 4px 10px ${
                muiTheme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.06)'
              }`,
            backgroundImage: 'none !important',
            backgroundColor: 'background.paper',
            backdropFilter: 'none !important',
            WebkitBackdropFilter: 'none !important',
            p: 0,
          },
        }}
        MenuListProps={{
          dense: true,
          sx: { py: 0.5 },
        }}
      >
        <MenuItem
          selected={selectedScheme === 'light'}
          onClick={() => handleSelect('light')}
          disableRipple
          sx={insetMenuItemSx}
        >
          <ListItemIcon>
            <SvgColor
              src={`${CONFIG.site.basePath}/assets/icons/setting/ic-light-mode-outlined.svg`}
              width={22}
              sx={{ color: 'text.primary' }}
            />
          </ListItemIcon>
          <ListItemText>{t('theme.light')}</ListItemText>
        </MenuItem>
        <MenuItem
          selected={selectedScheme === 'dark'}
          onClick={() => handleSelect('dark')}
          disableRipple
          sx={insetMenuItemSx}
        >
          <ListItemIcon>
            <SvgColor
              src={`${CONFIG.site.basePath}/assets/icons/setting/ic-dark-mode-outlined.svg`}
              width={22}
              sx={{ color: 'text.primary' }}
            />
          </ListItemIcon>
          <ListItemText>{t('theme.dark')}</ListItemText>
        </MenuItem>
        <MenuItem
          selected={selectedScheme === 'system'}
          onClick={() => handleSelect('system')}
          disableRipple
          sx={insetMenuItemSx}
        >
          <ListItemIcon>
            <SvgColor
              src={`${CONFIG.site.basePath}/assets/icons/setting/ic-system-mode-outlined.svg`}
              width={22}
              sx={{ color: 'text.primary' }}
            />
          </ListItemIcon>
          <ListItemText>{t('theme.system')}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
